/* eslint-disable import/no-duplicates */
import { FormHandles } from '@unform/core'
import { Form } from '@unform/web'
import {
  addMonths,
  addYears,
  differenceInMonths,
  format,
  parseISO,
} from 'date-fns'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiCheckCircle } from 'react-icons/fi'
import { useHistory } from 'react-router-dom'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ptBR } from 'date-fns/locale'

import simulationPercentageInfo from '../../assets/simulation-percentage-info.svg'
import simulationSelectPercentIcon from '../../assets/simulation-select-percent-icon.svg'
import simulationSelectTimeIcon from '../../assets/simulation-select-time-icon.svg'
import simulationYearsInfo from '../../assets/simulation-years-info.svg'
import Button from '../../components/Button'
import Container from '../../components/Container'
import Header from '../../components/Header'
import InputHidden from '../../components/InputHidden'
import InputSlider from '../../components/InputSlider'
import Line from '../../components/Line'
import usePersistedState from '../../hooks/usePersistedState'
import { formatValue } from '../../utils/formatValues'
import { UserData, UserDetails } from '../../utils/interfaces'
import { Valor } from '../../utils/masks'
import {
  BoxButtons,
  BtnVoltar,
  ButtonSelectBox,
  ButtonSimulationValue,
  ColoredBoxInfo,
  Content,
  GraphWrapper,
  InfoValuesBox,
  ReceiveBox,
  UPBoxSlider,
  UPContentSlider,
} from './styles'

interface CustomTooltipInterface {
  active: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  label: string
}

interface Values {
  year: number
  anomes: string
  invested: number
  finalBalance: number
  rentability: number
}

const Simulation: React.FC = () => {
  const [userDetails] = usePersistedState<UserDetails>(
    'userDetails',
    {} as UserDetails,
  )
  const [userData] = usePersistedState<UserData>('userData', {} as UserData)
  const [totalBalance, setTotalBalance] = usePersistedState('totalBalance', 0)

  const [timeValueYears] = usePersistedState<number>('TimeValueYears', 5)
  const [, setRendaFixa] = usePersistedState('RendaFixaValue', 0)

  const [selectedReceive, setSelectedReceive] = usePersistedState<
    'PD' | 'PS' | ''
  >('receiveTypeSelected', '')

  const [pctSaldo, setPctSaldo] = usePersistedState<number>(
    'pctSobreSaldo',
    0.1,
  )

  const [tipCtbBasica] = usePersistedState('tipCtbBasica', '') // P = Percentual sobre o salário || M = Máxima permitida pelo plano
  const [tipCtbAdic] = usePersistedState('tipCtbAdic', '') // P = Percentual sobre o salário || F = Valor fixo

  const [flagEdit] = usePersistedState<'S' | 'N'>('flagEdit', 'N')
  const [displayPercent, setDisplayPercent] = useState(selectedReceive === 'PS')
  const [displayTime, setDisplayTime] = useState(selectedReceive === 'PD')

  const [totalInvested, setTotalInvested] = useState(0)
  const [values, setValues] = useState<Values[]>([])
  const [launched, setLaunched] = useState(false)

  const [rentAnual, setRentAnual] = usePersistedState('rentAnual', 4.75)
  const [rentMensal, setRentMensal] = usePersistedState(
    'rentMensal',
    ((1 + rentAnual / 100) ** (1 / 12) - 1) * 100,
  )
  const [evolSalarial, setEvolSalarial] = usePersistedState('evolSal', 3)
  const history = useHistory()

  const [, setTotalInvestedPartic] = useState(0)
  const [, setTotalInvestedPatroc] = useState(0)
  const [USB] = usePersistedState<number>('usb', 322.93) // 292.54

  const percentualValue = useMemo(
    () => formatValue(totalBalance * (pctSaldo / 100)),
    [pctSaldo, totalBalance],
  )
  const timeValue = useMemo(
    () => formatValue(totalBalance / (timeValueYears * 12)),
    [timeValueYears, totalBalance],
  )
  const formRef = useRef<FormHandles>(null)

  const maxPctCtb = useCallback(
    salVlr => {
      let maxPct = 0
      if (salVlr <= USB * 23) {
        maxPct = 2
      } else if (salVlr > USB * 23 && salVlr <= USB * 46) {
        maxPct = 4
      } else if (salVlr > USB * 46 && salVlr <= USB * 69) {
        maxPct = 6
      } else if (salVlr > USB * 69 && salVlr <= USB * 92) {
        maxPct = 7
      } else if (salVlr > USB * 92) {
        maxPct = 8
      }
      return maxPct
    },
    [USB],
  )

  const createValues = useCallback(() => {
    const valuesCalculated: Values[] = []
    let totalInvestedTemp = 0
    let temporaryFinalBalance = 0
    let tempMonthInvestedPartic = 0
    let tempTotalInvestedPartic = 0
    let tempMonthInvestedPatroc = 0
    let tempTotalInvestedPatroc = 0
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let vlrRendeu = 0

    let { salario } = userDetails

    let pctCtbBasica = 0
    tipCtbBasica === 'M'
      ? (pctCtbBasica = maxPctCtb(salario))
      : (pctCtbBasica = userDetails.pctContribuicaoBasica)
    let vlrCtbBasica = salario * (pctCtbBasica / 100)
    let vlrCtbPatroc = vlrCtbBasica
    let vlrCtbAdicPct = salario * (userDetails.pctContribuicaoSuplementar / 100)
    let vlrCtbAdicFixa = userDetails.contribuicaoSuplementar
    const dataAposentadoria = addYears(
      parseISO(userData.birthdate),
      userDetails.age,
    )
    const hoje = new Date()
    const dtIniCtb = hoje.getDay() <= 15 ? hoje : addMonths(hoje, 1)
    const monthsContrib = differenceInMonths(dataAposentadoria, new Date())
    const totalContribution = monthsContrib // + yearsContrib
    let finalBalance = 0
    let counter = 1

    for (counter; counter <= totalContribution + 1; counter += 1) {
      const dataCorrente = addMonths(dtIniCtb, counter)
      const mesCorrente = dataCorrente.getMonth() + 1
      const anoCorrente = dataCorrente.getFullYear()

      /* EVOLUÇÃO SALARIAL */
      if (mesCorrente === 1) {
        salario += salario * (evolSalarial / 100)

        pctCtbBasica = 0
        tipCtbBasica === 'M'
          ? (pctCtbBasica = maxPctCtb(salario))
          : (pctCtbBasica = userDetails.pctContribuicaoBasica)

        vlrCtbBasica = salario * (pctCtbBasica / 100)
        vlrCtbPatroc = vlrCtbBasica
        vlrCtbAdicPct = salario * (userDetails.pctContribuicaoSuplementar / 100)
        vlrCtbAdicFixa = userDetails.contribuicaoSuplementar
      }
      // console.log(maxPctCtb(salario))
      const monthTotalInvested =
        vlrCtbBasica +
        vlrCtbPatroc +
        (tipCtbAdic === 'P' ? vlrCtbAdicPct : vlrCtbAdicFixa)

      totalInvestedTemp += monthTotalInvested

      finalBalance =
        temporaryFinalBalance * (rentMensal / 100) +
        (monthTotalInvested + temporaryFinalBalance)

      vlrRendeu = temporaryFinalBalance * (rentMensal / 100)

      // console.log(
      //   counter,
      //   `${mesCorrente}-${anoCorrente}`,
      //   idadeCorrente,
      //   formatValue(salario),
      //   formatValue(temporaryFinalBalance),
      //   formatValue(vlrCtbBasica),
      //   formatValue(vlrCtbPatroc),
      //   formatValue(vlrCtbAdicPct),
      //   formatValue(vlrCtbAdicFixa),
      //   formatValue(finalBalance),
      //   formatValue(vlrRendeu),
      // )

      temporaryFinalBalance = finalBalance

      /* Investimento do Participante */
      tempMonthInvestedPartic =
        vlrCtbBasica + (tipCtbAdic === 'P' ? vlrCtbAdicPct : vlrCtbAdicFixa)
      tempTotalInvestedPartic += tempMonthInvestedPartic

      /* Investimento da Patrocinadora */
      tempMonthInvestedPatroc = vlrCtbPatroc
      tempTotalInvestedPatroc += tempMonthInvestedPatroc

      let anomesFormat = format(dataCorrente, "MMMM' / 'yyyy", { locale: ptBR })
      anomesFormat = anomesFormat[0].toUpperCase() + anomesFormat.substring(1)

      const val: Values = {
        year: anoCorrente,
        anomes: anomesFormat,
        invested: totalInvestedTemp,
        rentability: rentMensal,
        finalBalance,
      }
      valuesCalculated.push(val)
    }

    setTotalInvested(totalInvestedTemp)
    setTotalBalance(temporaryFinalBalance)
    setTotalInvestedPartic(tempTotalInvestedPartic)
    setTotalInvestedPatroc(tempTotalInvestedPatroc)
    setValues(valuesCalculated)
    setLaunched(true)
  }, [
    evolSalarial,
    maxPctCtb,
    rentMensal,
    setTotalBalance,
    tipCtbAdic,
    tipCtbBasica,
    userData,
    userDetails,
  ])

  const toggleSelectedReceive = useCallback(
    (selectedReceiveNow: 'PD' | 'PS' | '') => {
      setSelectedReceive(selectedReceiveNow)
      if (selectedReceiveNow === 'PD') {
        setDisplayTime(true)
        setDisplayPercent(false)
      } else if (selectedReceiveNow === 'PS') {
        setDisplayTime(false)
        setDisplayPercent(true)
      } else {
        setDisplayPercent(false)
        setDisplayTime(false)
      }
    },
    [setSelectedReceive],
  )

  const aaaa = useCallback(() => {
    if (!launched) createValues()
  }, [createValues, launched])

  useEffect(() => {
    setLaunched(false)
    aaaa()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentMensal, evolSalarial])

  const graphValues = values.map(value => ({
    year: value.year,
    anomes: value.anomes,
    value: value.finalBalance,
    value2: value.invested,
    formattedValue: Valor(value.finalBalance.toFixed(2)),
  }))

  const CustomTooltip = useCallback(
    ({ active, payload }: CustomTooltipInterface) => {
      if (active) {
        return (
          <div className="tooltip">
            <h4>
              <b>{payload[0].payload.anomes}</b>
            </h4>
            <p>
              Saldo: <span>{Valor(payload[0].payload.value.toFixed(2))}</span>
            </p>
            <p>Investido: {Valor(payload[0].payload.value2.toFixed(2))}</p>
          </div>
        )
      }
      return null
    },
    [],
  )

  const handleSubmit = useCallback(
    async data => {
      if (data.tipo === 'PD' || data.tipo === 'PS') {
        setRendaFixa(0)
      }
      if (flagEdit === 'S') {
        history.push('/resume')
      } else {
        history.push('/pep')
      }
    },
    [flagEdit, history, setRendaFixa],
  )

  const marksRent = []
  for (let i = 0; i <= 10; i += 0.5) {
    marksRent.push({
      value: i,
      label: Math.floor(i) === i ? `${i.toString().replace('.', ',')}%` : '',
    })
  }
  const marksSal = []
  for (let i = 0; i <= 5; i += 0.5) {
    marksSal.push({
      value: i,
      label: i % 1 === 0 ? `${i.toString().replace('.', ',')}%` : '',
    })
  }
  const marksPctSaldo = []
  marksPctSaldo.push({
    value: 0.1,
    label: '0.1%',
  })
  for (let i = 0.1; i <= 1.51; i += 0.1) {
    const j = parseFloat(i.toFixed(1))
    marksPctSaldo.push({
      value: j,
      label: j % 0.5 === 0 ? `${j.toString().replace('.', ',')}%` : '',
    })
  }

  const handleChangeRentAnual = useCallback(
    value => {
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(value)) {
        setRentAnual(value)
        setRentMensal(((1 + value / 100) ** (1 / 12) - 1) * 100)
      }
    },
    [setRentAnual, setRentMensal],
  )
  const handleChangeEvolSalarial = useCallback(
    value => {
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(value)) {
        setEvolSalarial(value)
      }
    },
    [setEvolSalarial],
  )

  const handleChangePctSaldo = useCallback(
    value => {
      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(value)) {
        setPctSaldo(value)
      }
    },
    [setPctSaldo],
  )

  const handleConfirmValues = useCallback(() => {
    formRef.current?.submitForm()
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }, [])

  return (
    <>
      <Header />
      <Container>
        <Content>
          <div className="values-box">
            <InfoValuesBox color="green" gradientDirection="left">
              <span>Valor Investido</span>
              <h3>{formatValue(totalInvested)}</h3>
              <small>Seu investimento no período</small>
            </InfoValuesBox>

            <InfoValuesBox color="darkGreen" gradientDirection="left">
              <span>Seu saldo projetado</span>
              <h3>{formatValue(totalBalance)}</h3>
              <small>Seu investimento + rentabilidade</small>
            </InfoValuesBox>
          </div>
          <article>
            <p>
              Os valores demonstrados por este simulador são calculados
              assumindo diversas hipóteses e representam estimativas baseadas em
              informações correntes, não significando promessa de rentabilidade
              ou garantia de nível de benefícios. Assim, os resultados
              apresentados não representam o valor final do cálculo e não
              possuem valor legal.
            </p>
            <p>
              Por fim, ressaltamos que tais observações são aplicáveis também
              aos valores de impostos simulados e apresentados pela ferramenta
              para auxiliar no planejamento dos participantes.
            </p>
          </article>

          <GraphWrapper>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={graphValues}>
                <defs>
                  <linearGradient id="colorInvest" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6f81b7" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#6f81b7" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorRent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#69a578" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#69a578" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value2"
                  stroke="#024A94"
                  fillOpacity={1}
                  fill="url(#colorInvest)"
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#00793a"
                  fillOpacity={1}
                  fill="url(#colorRent)"
                />
                <Tooltip
                  content={
                    <CustomTooltip active={false} payload={2} label="" />
                  }
                />
                <XAxis name="Ano" dataKey="year" />
                <YAxis
                  name="Saldo"
                  dataKey="value"
                  width={75}
                  axisLine={false}
                  tickLine={false}
                  tickCount={5}
                  tickFormatter={number => `${Valor(number.toFixed(2))}`}
                />
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  opacity={0.5}
                />
              </AreaChart>
            </ResponsiveContainer>
          </GraphWrapper>
          <UPContentSlider>
            <p>Rentabilidade Anual Real Estimada</p>
            {/* <UPBoxSlider>
              <InputRange
                name="rangeRentabilidadeAnual"
                functionChange={e => handleChangeRentAnual(e)}
                start={0}
                end={10}
                initialValue={rentAnual}
                step={0.25}
                key={0}
                marks={marksRent}
                valueLabelDisplay="off"
              />
              <strong>{rentAnual}%</strong>
            </UPBoxSlider> */}
            <UPBoxSlider>
              <InputSlider
                name="rangeRentabilidadeAnual"
                functionChange={e => handleChangeRentAnual(e)}
                start={0}
                end={10}
                initialValue={rentAnual}
                step={0.05}
                key={0}
                marks={marksRent}
                valueLabelDisplay="off"
                sufix="%"
              />
            </UPBoxSlider>
            <small>{`Equivalente a ${rentMensal.toFixed(2)}% ao mes`}</small>
          </UPContentSlider>
          <UPContentSlider>
            <p>Crescimento Salarial Anual Real</p>
            {/* <UPBoxSlider>
              <InputRange
                name="rangeEvolucaoSalarial"
                functionChange={e => handleChangeEvolSalarial(e)}
                start={0}
                end={5}
                initialValue={evolSalarial}
                step={0.25}
                key={0}
                marks={marksSal}
                valueLabelDisplay="off"
              />
              <strong>{evolSalarial}%</strong>
            </UPBoxSlider> */}
            <UPBoxSlider>
              <InputSlider
                name="rangeEvolucaoSalarial"
                functionChange={e => handleChangeEvolSalarial(e)}
                start={0}
                end={5}
                initialValue={evolSalarial}
                step={0.05}
                key={0}
                marks={marksSal}
                valueLabelDisplay="off"
                sufix="%"
              />
            </UPBoxSlider>
          </UPContentSlider>
        </Content>
      </Container>
      <Container>
        <Content>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <strong>
              Escolha a forma de recebimento mensal <br /> do seu benefício:
            </strong>
            <ReceiveBox>
              <ButtonSelectBox
                type="button"
                onClick={() => toggleSelectedReceive('PS')}
                selected={selectedReceive === 'PS'}
              >
                <img src={simulationSelectPercentIcon} alt="Percentual" />
                <span>Percentual sobre o saldo</span>
                <FiCheckCircle />
              </ButtonSelectBox>
              <BoxButtons displayed={displayPercent}>
                <UPBoxSlider>
                  <InputSlider
                    name="rangeEvolucaoSalarial"
                    functionChange={e => handleChangePctSaldo(e)}
                    start={0.1}
                    end={1.5}
                    initialValue={pctSaldo}
                    step={0.01}
                    key={0}
                    marks={marksPctSaldo}
                    valueLabelDisplay="off"
                    sufix="%"
                  />
                  {/* <strong>{pctSaldo}%</strong> */}
                </UPBoxSlider>
                <small>Mínimo 0,1% | máximo 1,5%</small>
              </BoxButtons>
              <ColoredBoxInfo
                size="large"
                color="darkGreen"
                gradientDirection="left"
                displayed={displayPercent}
              >
                <div>
                  <h3>{percentualValue}*</h3>
                  <small>Valor recebido por mês</small>
                  <p>
                    *Valor simulado referente a {pctSaldo}% do seu saldo
                    projetado, com pagamentos mensais efetuados com base no
                    percentual que escolher no momento da aposentadoria.
                  </p>
                </div>
                <img src={simulationPercentageInfo} alt="Percentual" />
              </ColoredBoxInfo>
            </ReceiveBox>
            <Line />
            <ReceiveBox>
              <ButtonSelectBox
                type="button"
                onClick={() => toggleSelectedReceive('PD')}
                selected={selectedReceive === 'PD'}
              >
                <img src={simulationSelectTimeIcon} alt="Tempo" />
                <span>Prazo determinado</span>
                <FiCheckCircle />
              </ButtonSelectBox>
              <BoxButtons displayed={displayTime}>
                <ButtonSimulationValue type="button">
                  <span>5 anos</span>
                </ButtonSimulationValue>
              </BoxButtons>
              <ColoredBoxInfo
                size="large"
                color="green"
                gradientDirection="left"
                displayed={displayTime}
              >
                <div>
                  <h3>{timeValue}*</h3>
                  <small>Valor recebido por mês</small>
                  <p>
                    *Valor simulado referente a modalidade de prazo determinado
                    com duração de 5 anos calculado com base no seu saldo
                    projetado.
                  </p>
                </div>
                <img src={simulationYearsInfo} alt="Tempo" />
              </ColoredBoxInfo>
            </ReceiveBox>
            <InputHidden name="tipo" type="hidden" value={selectedReceive} />
            <small className="comment">
              Selecione uma das opções acima para simular o valor que você
              receberá mensalmente ao se aposentar
            </small>
          </Form>
        </Content>
        <Button
          type="button"
          fontSize="normal"
          color="orange"
          width="large"
          onClick={handleConfirmValues}
          disabled={!selectedReceive}
        >
          É isso que eu quero!
        </Button>

        <BtnVoltar type="button" onClick={() => history.push('/contribution')}>
          &lt; Quero alterar os valores e simular novamente
        </BtnVoltar>
      </Container>
    </>
  )
}

export default Simulation
